/**
 * Parallax für Bilder, gesteuert über ein Attribut direkt am Bild.
 *
 * In Webflow am `Visual Image` über dessen Attribut-Props setzen:
 *
 *   data-parallax="scroll"  Bild läuft langsamer als die Seite (Zwischenbild)
 *   data-parallax="zoom"    Bild wächst beim Herausscrollen leicht (Hero)
 *
 * Braucht kein data-module, die App startet das Modul, sobald ein Element mit
 * dem Attribut existiert. Der Rahmen um das Bild muss overflow: hidden haben,
 * sonst sieht man beim Verschieben die Kante.
 *
 * Aus bei reduzierter Bewegung und im Webflow-Designer. Animiert wird nur
 * transform, will-change steht nur, solange das Bild im Blick ist.
 */
import gsap, { reduced as systemReduziert, ScrollTrigger } from "@lib/gsap";
import { handleEditor } from "@webflow/detect-editor";

export type ParallaxArt = "scroll" | "zoom";

const ARTEN: readonly ParallaxArt[] = ["scroll", "zoom"];

// Bild ist um diesen Faktor größer als der Rahmen, damit beim Verschieben
// keine Kante sichtbar wird. 1,2 heißt 10 % Reserve oben und unten.
const SCROLL_SKALIERUNG = 1.2;
// Verschiebung in Prozent der Bildhöhe, muss unter der Reserve bleiben.
const SCROLL_VERSATZ = 8;
const ZOOM_ENDE = 1.1;

export interface ParallaxZiel {
  el: HTMLElement;
  art: ParallaxArt;
}

/** Findet alle Bilder mit gültigem data-parallax, unbekannte Werte fallen raus. */
export function sammleParallax(wurzel: ParentNode = document): ParallaxZiel[] {
  const ziele: ParallaxZiel[] = [];
  wurzel.querySelectorAll<HTMLElement>("[data-parallax]").forEach((el) => {
    const art = el.dataset.parallax?.trim().toLowerCase() as ParallaxArt;
    if (ARTEN.includes(art)) ziele.push({ el, art });
    else console.warn(`[parallax] Unbekannter Wert "${el.dataset.parallax}", erlaubt: ${ARTEN.join(", ")}`);
  });
  return ziele;
}

function rahmenVon(el: HTMLElement): HTMLElement {
  return el.closest<HTMLElement>("section") ?? el.parentElement ?? el;
}

function willChange(el: HTMLElement) {
  return (self: ScrollTrigger) => {
    el.style.willChange = self.isActive ? "transform" : "";
  };
}

function baue({ el, art }: ParallaxZiel): gsap.core.Tween {
  const trigger = rahmenVon(el);

  if (art === "zoom") {
    return gsap.fromTo(
      el,
      { scale: 1 },
      {
        scale: ZOOM_ENDE,
        ease: "none",
        scrollTrigger: { trigger, start: "top top", end: "bottom top", scrub: true, onToggle: willChange(el) },
      }
    );
  }

  return gsap.fromTo(
    el,
    { yPercent: -SCROLL_VERSATZ, scale: SCROLL_SKALIERUNG },
    {
      yPercent: SCROLL_VERSATZ,
      scale: SCROLL_SKALIERUNG,
      ease: "none",
      scrollTrigger: { trigger, start: "top bottom", end: "bottom top", scrub: true, onToggle: willChange(el) },
    }
  );
}

let laufend: gsap.core.Tween[] = [];

/** Räumt alle Parallax-Animationen ab und setzt die Bilder zurück. */
export function stoppeParallax() {
  laufend.forEach((tween) => {
    const ziele = tween.targets() as HTMLElement[];
    tween.scrollTrigger?.kill();
    tween.kill();
    gsap.set(ziele, { clearProps: "transform,willChange" });
  });
  laufend = [];
}

/**
 * Startet Parallax für alle gültigen Bilder. Gibt die Zahl der gestarteten
 * Animationen zurück (0 bei reduzierter Bewegung).
 */
export function starteParallax({
  wurzel = document as ParentNode,
  reduziert = systemReduziert,
}: { wurzel?: ParentNode; reduziert?: boolean } = {}): number {
  stoppeParallax();
  if (reduziert) return 0;
  laufend = sammleParallax(wurzel).map(baue);
  return laufend.length;
}

export function initParallax() {
  handleEditor((imDesigner) => {
    if (imDesigner) stoppeParallax();
    else starteParallax();
  });
}
